// src/campaigns/components/CampaignModal.tsx

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CampaignDefinition } from "../types/campaign.types";
import { createCampaignCreatePath } from "../engine/campaign-helpers";

interface CampaignModalProps {
  campaign: CampaignDefinition | null;
}

export function CampaignModal({ campaign }: CampaignModalProps) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const storageKey = useMemo(
    () => (campaign ? `lrnsy_campaign_closed_${campaign.id}` : ""),
    [campaign]
  );

  useEffect(() => {
    if (!campaign || !storageKey) {
      setIsVisible(false);
      return;
    }

    const isClosed = sessionStorage.getItem(storageKey) === "true";

    if (!isClosed) {
      const timer = window.setTimeout(() => setIsVisible(true), 900);
      return () => window.clearTimeout(timer);
    }

    setIsVisible(false);
  }, [campaign, storageKey]);

  if (!campaign) return null;

  const close = () => {
    sessionStorage.setItem(storageKey, "true");
    setIsVisible(false);
  };

  const create = () => {
    sessionStorage.setItem(storageKey, "true");
    setIsVisible(false);
    navigate(createCampaignCreatePath(campaign));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="absolute inset-0 bg-black/62 backdrop-blur-md"
          />

          <motion.div
            initial={{ opacity: 0, y: 26, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="
              relative w-full max-w-md overflow-hidden rounded-[34px]
              border border-white/10 bg-black/70 px-6 py-7 text-center
              shadow-[0_34px_100px_rgba(0,0,0,0.65)]
            "
          >
            <div
              className="absolute inset-0 opacity-45"
              style={{
                background: `radial-gradient(circle at 50% 0%, ${campaign.theme.glow} 0%, transparent 58%)`,
              }}
            />

            <button
              onClick={close}
              className="
                absolute right-4 top-4 z-20 rounded-full border border-white/10
                bg-white/5 p-2 text-white/45 transition-colors hover:text-white
              "
              aria-label="Close campaign"
              type="button"
            >
              <X size={15} />
            </button>

            <div className="relative z-10">
              <p
                className="mb-4 text-[9px] font-bold uppercase tracking-[0.38em]"
                style={{ color: campaign.theme.primary }}
              >
                {campaign.name}
              </p>

              <h2 className="mx-auto max-w-sm font-serif text-[30px] italic leading-tight text-white/95 sm:text-[36px]">
                {campaign.copy.modalTitle}
              </h2>

              <p className="mx-auto mt-4 max-w-xs text-sm leading-[1.8] text-white/58">
                {campaign.copy.modalDescription}
              </p>

              <motion.button
                onClick={create}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="
                  mx-auto mt-7 flex items-center justify-center gap-3
                  rounded-full px-7 py-4 text-[10px] font-black uppercase
                  tracking-[0.16em] text-black
                "
                style={{
                  background: `linear-gradient(135deg, #ffffff, ${campaign.theme.primary})`,
                  boxShadow: `0 22px 48px -20px ${campaign.theme.primary}`,
                }}
                type="button"
              >
                <span>{campaign.copy.modalCTA}</span>
                <ArrowRight size={15} />
              </motion.button>

              <button
                onClick={close}
                className="
                  mt-5 font-serif text-sm italic text-white/38 underline
                  underline-offset-4 transition-colors hover:text-white/65
                "
                type="button"
              >
                I’ll explore first
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
